import { useState } from "react";
import { motion } from "framer-motion";
import { fadeIn } from "../components/common/motionFrameVarients";
import Footer from "../components/common/Footer";
import HighlightText from "../components/core/HomePage/HighlightText";
import CTAButton from "../components/core/HomePage/Button";

// Sample data for upcoming events
const upcomingEvents = [
  {
    id: 1,
    title: "Web Development Bootcamp",
    date: "May 15, 2025",
    time: "10:00 AM - 4:00 PM EST",
    location: "Online (Zoom)",
    description: "Join our intensive one-day bootcamp covering modern web development frameworks and practices. Perfect for beginners and intermediate developers looking to level up their skills.",
    speaker: "Sarah Johnson",
    speakerRole: "Senior Developer, Tech Innovators",
    image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
  },
  {
    id: 2,
    title: "AI & Machine Learning Workshop",
    date: "May 22, 2025",
    time: "1:00 PM - 5:00 PM EST",
    location: "Kode Vedic HQ, New York",
    description: "Explore the fundamentals of AI and machine learning in this hands-on workshop. You'll learn about neural networks, data preprocessing, and build your first ML model.",
    speaker: "Dr. Michael Chen",
    speakerRole: "AI Researcher, DataMind",
    image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
  },
  {
    id: 3,
    title: "Coding for Good Hackathon",
    date: "June 5-7, 2025",
    time: "Starts at 9:00 AM Friday",
    location: "Tech Hub, San Francisco",
    description: "A 48-hour hackathon focused on creating technology solutions for social good. Team up with other developers to solve real-world problems faced by non-profit organizations.",
    speaker: "Various Industry Mentors",
    speakerRole: "",
    image: "https://images.pexels.com/photos/3153201/pexels-photo-3153201.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
  }
];

// Sample data for past events with testimonials
const pastEvents = [
  {
    id: 1,
    title: "JavaScript Deep Dive Conference",
    date: "March 18, 2025",
    attendees: 780,
    image: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=600",
    testimonial: {
      quote: "The JavaScript conference was incredibly insightful. I learned advanced techniques that I've already implemented in my work projects.",
      author: "Alex Rivera",
      role: "Frontend Developer"
    }
  },
  {
    id: 2,
    title: "Women in Tech Summit",
    date: "February 25, 2025",
    attendees: 450,
    image: "https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
    testimonial: {
      quote: "An empowering experience that connected me with amazing women leaders in the tech industry. The mentorship opportunities were invaluable.",
      author: "Priya Sharma",
      role: "Software Engineer"
    }
  }
];

// Sample FAQ data
const faqs = [
  {
    question: "How can I register for events?",
    answer: "Registration is available through our website. Simply navigate to the specific event page and click the 'Register' button. You'll receive a confirmation email with all necessary details."
  },
  {
    question: "Are the events free to attend?",
    answer: "Many of our community events are free for Kode Vedic students. For larger conferences and workshops, there may be a fee, but we offer early bird discounts and student pricing."
  },
  {
    question: "Can I propose a talk or workshop?",
    answer: "Absolutely! We welcome community contributions. Visit our 'Become a Speaker' page to submit your proposal for review by our content team."
  },
  {
    question: "Will recordings be available after the events?",
    answer: "Yes, recordings of online events are typically made available to registered participants within 48 hours. Some content may also be published on our YouTube channel later."
  },
  {
    question: "How can I sponsor an event?",
    answer: "We offer various sponsorship packages for businesses interested in reaching our community. Please contact our partnerships team at partnerships@kodevedic.com for more information."
  }
];

function CommunityEventsPage() {
  const [activeTab, setActiveTab] = useState("upcoming");
  const [activeFaq, setActiveFaq] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const openEventDetails = (event) => {
    setSelectedEvent(event);
  };

  const closeEventDetails = () => {
    setSelectedEvent(null);
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.1 }}
      className="w-full overflow-hidden bg-richblack-900 text-white"
    >
      {/* Hero Section */}
      <div className="relative w-full h-[70vh]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-richblack-900/90 flex items-center justify-center">
          <div className="text-center px-4 w-11/12 max-w-maxContent">
            <motion.h1
              variants={fadeIn("up", 0.2)}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              Join Our <HighlightText text="Community Events" />
            </motion.h1>
            <motion.p
              variants={fadeIn("up", 0.4)}
              className="text-richblack-200 text-lg md:text-xl max-w-4xl mx-auto mb-8"
            >
              Connect, learn, and grow with fellow developers at our workshops, hackathons, and conferences
            </motion.p>
            <motion.div variants={fadeIn("up", 0.6)} className="flex flex-col sm:flex-row justify-center gap-4">
              <CTAButton
                active={true}
                linkto="#upcoming-events"
              >
                Explore Events
              </CTAButton>
              <CTAButton
                active={false}
                linkto="/community/host-event"
              >
                Host an Event
              </CTAButton>
            </motion.div>
          </div>
        </div>
        <img 
          src="https://images.pexels.com/photos/2608517/pexels-photo-2608517.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
          alt="Community event with people" 
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Navigation Tabs */}
      <div className="bg-richblack-800 sticky top-16 z-10">
        <div className="w-11/12 max-w-maxContent mx-auto flex overflow-x-auto">
          <button 
            onClick={() => setActiveTab("upcoming")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "upcoming" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            Upcoming Events
          </button>
          <button 
            onClick={() => setActiveTab("past")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "past" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            Past Events
          </button>
          <button 
            onClick={() => setActiveTab("calendar")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "calendar" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            Event Calendar
          </button>
          <button 
            onClick={() => setActiveTab("faq")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "faq" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            FAQ
          </button>
        </div>
      </div>
      
      {/* Upcoming Events */}
      <section id="upcoming-events" className={`w-11/12 max-w-maxContent mx-auto py-16 scroll-mt-24 ${activeTab !== "upcoming" ? "hidden" : ""}`}>
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Upcoming <HighlightText text="Events" />
          </h2>
          <p className="text-richblack-300 max-w-3xl">
            Expand your knowledge, network with peers, and stay up-to-date with the latest technologies at our upcoming events.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {upcomingEvents.map((event, index) => (
            <motion.div
              key={event.id}
              variants={fadeIn("up", 0.3 + index * 0.1)}
              className="bg-richblack-800 rounded-xl overflow-hidden cursor-pointer hover:transform hover:scale-[1.02] transition-all duration-300"
              onClick={() => openEventDetails(event)}
            >
              <div className="h-48 overflow-hidden">
                <img 
                  src={event.image} 
                  alt={event.title} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-yellow-50 text-sm mb-2">
                  <span className="font-medium">{event.date}</span>
                  <span>•</span>
                  <span>{event.location}</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{event.title}</h3>
                <p className="text-richblack-300 mb-4 line-clamp-3">{event.description}</p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-richblack-300">
                    <span className="font-medium text-yellow-50">{event.speaker}</span>
                    {event.speakerRole && <span> • {event.speakerRole}</span>}
                  </div>
                  <button className="text-yellow-50 text-sm font-medium">Details →</button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Event Details Modal */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-richblack-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="relative h-64">
                <img 
                  src={selectedEvent.image} 
                  alt={selectedEvent.title} 
                  className="w-full h-full object-cover"
                />
                <button 
                  onClick={closeEventDetails}
                  className="absolute top-4 right-4 bg-black/50 rounded-full w-8 h-8 flex items-center justify-center text-white"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 text-yellow-50 text-sm mb-2">
                  <span className="font-medium">{selectedEvent.date}</span>
                  <span>•</span>
                  <span>{selectedEvent.time}</span>
                  <span>•</span>
                  <span>{selectedEvent.location}</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">{selectedEvent.title}</h3>
                <p className="text-richblack-100 mb-6">{selectedEvent.description}</p>
                
                <div className="mb-6">
                  <h4 className="text-lg font-medium mb-2">Speaker</h4>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-richblack-700 rounded-full flex items-center justify-center text-lg font-bold">
                      {selectedEvent.speaker.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium">{selectedEvent.speaker}</p>
                      {selectedEvent.speakerRole && <p className="text-sm text-richblack-300">{selectedEvent.speakerRole}</p>}
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <CTAButton
                    active={true}
                    linkto={`/events/register/${selectedEvent.id}`}
                    className="px-6"
                  >
                    Register Now
                  </CTAButton>
                  <button 
                    onClick={closeEventDetails}
                    className="text-richblack-300 underline"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <motion.div
          variants={fadeIn("up", 0.6)}
          className="mt-12 text-center"
        >
          <CTAButton
            active={false}
            linkto="/community/events/all"
          >
            View All Events
          </CTAButton>
        </motion.div>
      </section>
      
      {/* Past Events */}
      <section id="past-events" className={`bg-richblack-800 py-16 scroll-mt-24 ${activeTab !== "past" ? "hidden" : ""}`}>
        <div className="w-11/12 max-w-maxContent mx-auto">
          <motion.div
            variants={fadeIn("up", 0.2)}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Past <HighlightText text="Events" />
            </h2>
            <p className="text-richblack-300 max-w-3xl">
              Take a look at our previous events and see what the community has to say about their experiences.
            </p>
          </motion.div>
          
          <div className="space-y-16">
            {pastEvents.map((event, index) => (
              <motion.div
                key={event.id}
                variants={fadeIn("up", 0.3 + index * 0.1)}
                className="grid md:grid-cols-5 gap-8 items-center"
              >
                <div className="md:col-span-2">
                  <img 
                    src={event.image} 
                    alt={event.title} 
                    className="w-full h-64 object-cover rounded-xl"
                  />
                </div>
                <div className="md:col-span-3">
                  <div className="text-yellow-50 text-sm mb-2">{event.date}</div>
                  <h3 className="text-2xl font-bold mb-2">{event.title}</h3>
                  <p className="text-richblack-300 mb-4">{event.attendees} participants attended</p>
                  
                  <div className="bg-richblack-700 p-4 rounded-lg mb-6">
                    <p className="italic text-richblack-100 mb-3">"{event.testimonial.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-richblack-600 rounded-full flex items-center justify-center text-sm font-bold">
                        {event.testimonial.author.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{event.testimonial.author}</p>
                        <p className="text-xs text-richblack-300">{event.testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <CTAButton
                      active={false}
                      linkto={`/events/gallery/${event.id}`}
                    >
                      View Gallery
                    </CTAButton>
                    <CTAButton
                      active={false}
                      linkto={`/events/recordings/${event.id}`}
                    >
                      Watch Recordings
                    </CTAButton>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            variants={fadeIn("up", 0.6)}
            className="mt-12 text-center"
          >
            <CTAButton
              active={false}
              linkto="/community/events/archive"
            >
              Browse Event Archive
            </CTAButton>
          </motion.div>
        </div>
      </section>
      
      {/* Event Calendar */}
      <section id="calendar" className={`w-11/12 max-w-maxContent mx-auto py-16 scroll-mt-24 ${activeTab !== "calendar" ? "hidden" : ""}`}>
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Event <HighlightText text="Calendar" />
          </h2>
          <p className="text-richblack-300 max-w-3xl">
            Plan ahead with our comprehensive event calendar. Filter by event type, location, or topic.
          </p>
        </motion.div>
        
        <motion.div
          variants={fadeIn("up", 0.3)}
          className="bg-richblack-800 p-6 rounded-xl mb-8"
        >
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-50"></div>
              <span className="text-sm text-richblack-300">Workshops</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-400"></div>
              <span className="text-sm text-richblack-300">Conferences</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-400"></div>
              <span className="text-sm text-richblack-300">Hackathons</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-pink-400"></div>
              <span className="text-sm text-richblack-300">Meetups</span>
            </div>
          </div>
          
          <div className="bg-richblack-900 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-7 text-center text-sm text-richblack-300 mb-2">
              <div>Sun</div>
              <div>Mon</div>
              <div>Tue</div>
              <div>Wed</div>
              <div>Thu</div>
              <div>Fri</div>
              <div>Sat</div>
            </div>
            
            <div className="grid grid-cols-7 gap-1 text-center">
              {/* Sample calendar dates */}
              {Array.from({ length: 31 }, (_, i) => (
                <div key={i} className="aspect-square flex items-center justify-center">
                  <button 
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm
                      ${i === 14 ? "bg-yellow-50 text-richblack-900" : ""}
                      ${i === 21 ? "bg-blue-400 text-white" : ""}
                      ${i === 4 ? "bg-green-400 text-white" : ""}
                      ${i === 9 ? "bg-pink-400 text-white" : ""}
                      ${![14, 21, 4, 9].includes(i) ? "hover:bg-richblack-700" : ""}
                    `}
                  >
                    {i + 1}
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-richblack-700 p-4 rounded-lg border-l-4 border-yellow-50">
              <div className="text-sm text-yellow-50">May 15</div>
              <h4 className="font-medium mb-1">Web Development Bootcamp</h4>
              <p className="text-xs text-richblack-300">10:00 AM - 4:00 PM</p>
            </div>
            <div className="bg-richblack-700 p-4 rounded-lg border-l-4 border-blue-400">
              <div className="text-sm text-blue-400">May 22</div>
              <h4 className="font-medium mb-1">AI & Machine Learning Workshop</h4>
              <p className="text-xs text-richblack-300">1:00 PM - 5:00 PM</p>
            </div>
            <div className="bg-richblack-700 p-4 rounded-lg border-l-4 border-green-400">
              <div className="text-sm text-green-400">June 5-7</div>
              <h4 className="font-medium mb-1">Coding for Good Hackathon</h4>
              <p className="text-xs text-richblack-300">9:00 AM Friday - 9:00 AM Sunday</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          variants={fadeIn("up", 0.4)}
          className="flex justify-center gap-4"
        >
          <CTAButton
            active={true}
            linkto="/community/events/calendar/subscribe"
          >
            Subscribe to Calendar
          </CTAButton>
          <CTAButton
            active={false}
            linkto="/community/events/calendar/ical"
          >
            Download iCal File
          </CTAButton>
        </motion.div>
      </section>
      
      {/* FAQ Section */}
      <section id="faq" className={`bg-richblack-800 py-16 scroll-mt-24 ${activeTab !== "faq" ? "hidden" : ""}`}>
        <div className="w-11/12 max-w-maxContent mx-auto">
          <motion.div
            variants={fadeIn("up", 0.2)}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked <HighlightText text="Questions" />
            </h2>
          </motion.div>
          
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                variants={fadeIn("up", 0.3 + index * 0.1)}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className={`w-full text-left p-5 flex justify-between items-center ${activeFaq === index ? "bg-richblack-700 rounded-t-lg" : "bg-richblack-900 rounded-lg"}`}
                >
                  <h3 className="font-medium text-lg">{faq.question}</h3>
                  <span className="text-xl">
                    {activeFaq === index ? "−" : "+"}
                  </span>
                </button>
                
                {activeFaq === index && (
                  <div className="bg-richblack-700 p-5 rounded-b-lg">
                    <p className="text-richblack-100">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          
          <motion.div
            variants={fadeIn("up", 0.6)}
            className="mt-12 text-center"
          >
            <p className="text-richblack-300 mb-4">Still have questions?</p>
            <CTAButton
              active={true}
              linkto="/community/contact"
            >
              Contact Support
            </CTAButton>
          </motion.div>
        </div>
      </section>
      
      {/* Community Involvement */}
      <section className="w-11/12 max-w-maxContent mx-auto py-16">
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Get <HighlightText text="Involved" />
          </h2>
          <p className="text-richblack-300 max-w-3xl">
            There are many ways to participate in our community events. Choose what works best for you!
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <motion.div
            variants={fadeIn("up", 0.3)}
            className="bg-richblack-800 p-6 rounded-xl"
          >
            <div className="text-4xl mb-4">👨‍💻</div>
            <h3 className="text-xl font-bold mb-3">Become a Speaker</h3>
            <p className="text-richblack-300 mb-6">
              Share your expertise with our community. Submit a talk proposal for one of our upcoming events.
            </p>
            <CTAButton
              active={false}
              linkto="/community/apply-speaker"
              className="w-full text-center"
            >
              Apply as Speaker
            </CTAButton>
          </motion.div>
          
          <motion.div
            variants={fadeIn("up", 0.4)}
            className="bg-richblack-800 p-6 rounded-xl"
          >
            <div className="text-4xl mb-4">🏆</div>
            <h3 className="text-xl font-bold mb-3">Volunteer</h3>
            <p className="text-richblack-300 mb-6">
              Help organize and run our events. It's a great way to network and give back to the community.
            </p>
            <CTAButton
              active={false}
              linkto="/community/volunteer"
              className="w-full text-center"
            >
              Volunteer with Us
            </CTAButton>
          </motion.div>
          
          <motion.div
            variants={fadeIn("up", 0.5)}
            className="bg-richblack-800 p-6 rounded-xl"
          >
            <div className="text-4xl mb-4">🚀</div>
            <h3 className="text-xl font-bold mb-3">Sponsor an Event</h3>
            <p className="text-richblack-300 mb-6">
              Showcase your brand to our community of developers and tech enthusiasts through event sponsorship.
            </p>
            <CTAButton
              active={false}
              linkto="/community/sponsor"
              className="w-full text-center"
            >
              Become a Sponsor
            </CTAButton>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="bg-gradient-to-r from-richblack-900 to-richblack-800 py-16">
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="w-11/12 max-w-maxContent mx-auto text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Join Our <HighlightText text="Community" />
          </h2>
          <p className="text-richblack-300 mb-8 max-w-2xl mx-auto">
            Connect with fellow developers, stay updated on upcoming events, and become part of our growing tech community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CTAButton
              active={true}
              linkto="/community/join"
            >
              Join Community
            </CTAButton>
            <CTAButton
              active={false}
              linkto="/community/newsletter"
            >
              Subscribe to Newsletter
            </CTAButton>
          </div>
        </motion.div>
      </section>

      <Footer />
      
    </motion.div>
  );
}

export default CommunityEventsPage;